import { getAPIClient, LegacyGetRecordDataDtoWithIntId } from "../apiClient";
import {
  appSettingsAtom,
  izRecordAPIResultAtomFamily,
  izSearchAPIResultAtom,
  screeningsAPIResultAtomFamily,
  shLinksAPIResultAtom,
  shLinkPasscodeAtomFamily,
  userAtom,
  verifiedSearchesAtom,
  avatarAtomFamily,
  devicesAtom,
  isHydrated,
} from "./atoms";
import { errorLog, warnLog } from "../utils/logger";
import { globalStore } from "./store";
import Bugsnag from "@bugsnag/js";
import { hasScreeningsEnabled } from "../globals";

export async function updateSearchAtoms(refresh: boolean): Promise<boolean> {
  try {
    const searches = await getAPIClient().getIzSearches(refresh);
    if (!searches.data) {
      return true;
    }

    await globalStore.set(izSearchAPIResultAtom, searches.data);
    // If we updated the searches we should also update the records
    return updateRecordAtoms();
  } catch (error: any) {
    // Handle error here, should be silent though
    errorLog(error, "Could not update search atoms");
    return false;
  }
}

export async function updateRecordAtoms(): Promise<boolean> {
  try {
    const searchesResult = await globalStore.get(verifiedSearchesAtom);

    if (!isHydrated(searchesResult)) {
      return true;
    }

    for (const search of searchesResult) {
      try {
        // Use the family of atoms for records
        const records: LegacyGetRecordDataDtoWithIntId = await getAPIClient().getIzRecord(
          search.uid,
          search.izProviderKey
        );
        const izRecordAPIResultAtom = izRecordAPIResultAtomFamily(search.uid);
        await globalStore.set(izRecordAPIResultAtom, records);
      } catch (e: any) {
        if (e instanceof Error) {
          errorLog(e, `Could not retrieve record for search ${search.uid}: ${e.message}`);
          Bugsnag.notify(e);
        } else {
          warnLog(`Could not retrieve record for search ${search.uid}: ${e}`);
        }
      }
    }

    return (await updateScreeningAtom()) && (await updateSmartHealthLinkAtoms());
  } catch (error: any) {
    errorLog(error, "Could not update record atoms");
    return false;
  }
}

export async function updateSmartHealthLinkAtoms(): Promise<boolean> {
  const searchesResult = await globalStore.get(verifiedSearchesAtom);

  if (!isHydrated(searchesResult)) {
    return true;
  }

  const links = await getAPIClient().getSmartHealthLinks();
  await globalStore.set(shLinksAPIResultAtom, links);

  return true;
}

export async function setSmartHealthLinkPasscode(id: string, passcode: string): Promise<boolean> {
  const shLinkPasscodeAtom = await shLinkPasscodeAtomFamily(id);
  await globalStore.set(shLinkPasscodeAtom, { id, passcode });
  return true;
}

export async function deleteSmartHealthLinkPasscode(id: string): Promise<boolean> {
  const shLinkPasscodeAtom = await shLinkPasscodeAtomFamily(id);
  await globalStore.set(shLinkPasscodeAtom, null);
  return true;
}

export async function setRecordAvatar(
  recordUid: string,
  avatarId: number | null
): Promise<boolean> {
  try {
    const avatarAtom = await avatarAtomFamily(recordUid);
    await globalStore.set(avatarAtom, avatarId);
    return true;
  } catch (error: any) {
    errorLog(error, "Could not update record avatar");
    return false;
  }
}

export async function updateDevicesAtom(): Promise<boolean> {
  try {
    const devices = await getAPIClient().getDevices();
    await globalStore.set(devicesAtom, devices);
    return true;
  } catch (error: any) {
    errorLog(error, "Could not update devices atom");
    return false;
  }
}

export async function updateScreeningAtom(): Promise<boolean> {
  try {
    const searchesResult = await globalStore.get(verifiedSearchesAtom);
    if (!isHydrated(searchesResult)) {
      return true;
    }

    for (const search of searchesResult) {
      try {
        const screenings = await getAPIClient().getIzScreeningRecord(
          search.uid,
          search.izProviderKey
        );
        const izScreeningAPIResultAtom = screeningsAPIResultAtomFamily(search.uid);
        await globalStore.set(izScreeningAPIResultAtom, screenings);
      } catch (e: any) {
        if (e instanceof Error) {
          errorLog(e, `Could not retrieve screenings for search ${search.uid}: ${e.message}`);
          Bugsnag.notify(e);
        } else {
          warnLog(`Could not retrieve screenings for search ${search.uid}: ${e}`);
        }
      }
    }

    return true;
  } catch (error: any) {
    errorLog(error, "Could not update screening atom");
    return false;
  }
}

export async function clearPrefs(): Promise<void> {
  // We want to preserve the firebase emulator setting and whether or not this has NJ features
  const currentSettings = await globalStore.get(appSettingsAtom);
  await globalStore.set(appSettingsAtom, {
    ...currentSettings,
    email: "",
    authorized: false,
    signedInWithFirebase: false,
  });
}
